/*
 * rwdImageMaps AngularJS Directive v1.0
 *
 * Allows image maps to be used in a responsive design by recalculating the area coordinates to match the actual image size on load and window.resize
 *
 * Original Copyright (c) 2013 Matt Stow
 * https://github.com/stowball/jQuery-rwdImageMaps
 * http://mattstow.com
 * Licensed under the MIT license
 *
 * angular-rwdImageMaps.js (by Philip Saa)
 * https://github.com/cowglow/
 * @cowglow
 */

import { AfterViewInit, Directive, ElementRef, HostListener, Input } from '@angular/core';

const COORDS = 'coords';

@Directive({
  selector: '[rwdImgMap]',
  standalone: false
})
export class RwdImgMapDirective implements AfterViewInit {
  @Input('rwdImgMap') imgSrc;
  @Input() map: HTMLMapElement;
  originalCoords = [];
  originalWidth: number;
  originalHeight: number;
  mapElementName: string;

  constructor(private el: ElementRef) {}

  ngAfterViewInit() {
    this.originalWidth = parseInt(this.el.nativeElement.getAttribute('width'), 10);
    this.originalHeight = parseInt(this.el.nativeElement.getAttribute('height'), 10);

    if (!this.originalWidth || !this.originalHeight) {
      const imgUrl = this.imgSrc || this.el.nativeElement.getAttribute('src');
      if (!imgUrl) {
        return;
      }
      const tempImg = new Image();
      tempImg.src = imgUrl;
      tempImg.onload = () => {
        if (!this.originalWidth) {
          this.originalWidth = tempImg.width;
        }
        if (!this.originalHeight) {
          this.originalHeight = tempImg.height;
        }
        this.resize();
      };
    }

    this.mapElementName = this.el.nativeElement.getAttribute('usemap').replace('#', '');

    this.readAreaCoordinates();
  }

  @HostListener('window:resize')
  resize() {
    if (this.originalCoords.length === 0) {
      this.readAreaCoordinates();
    }
    if (!this.originalWidth || !this.originalHeight || this.originalCoords.length === 0) {
      return;
    }

    const scaleFactorX = this.el.nativeElement.width / this.originalWidth;
    const scaleFactorY = this.el.nativeElement.height / this.originalHeight;

    for (let i = 0; i < this.map.areas.length; i++) {
      const area = this.map.areas[i];

      const adjustedCoords = this.originalCoords[i]
        .map((value, coordinateIndex) => {
          return coordinateIndex % 2 === 0 ? Math.round(value * scaleFactorX) : Math.round(value * scaleFactorY);
        })
        .join(',');
      area.setAttribute(COORDS, adjustedCoords);
    }
  }

  readAreaCoordinates() {
    for (let i = 0; i < this.map.areas.length; i++) {
      const area = this.map.areas[i];
      const commaSeparatedCoords = area.getAttribute(COORDS);
      if (commaSeparatedCoords) {
        const coords: number[] = commaSeparatedCoords.split(',').map((value) => parseInt(value, 10));
        this.originalCoords.push(coords);
      }
    }
  }
}
